import React, { Component } from 'react';
import { connect } from 'react-redux';
import DocumentTitle from 'react-document-title';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { withTranslation, Trans } from 'react-i18next';

import { makeGetProject } from '../selectors/projects';
import { makeGetProjectNotificationsByType } from '../selectors/notifications';
import { makeGetSortedActiveProjectMilestones, makeGetSortedArchivedProjectMilestones } from '../selectors/milestones';
import { makeGetSkippedHelp } from '../selectors/app-props';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';
import { makeGetCurrentUser } from '../selectors/users';
import { makeGetCustomFields, makeGetCustomField } from 'selectors/custom-fields';

import { fetchMilestones } from '../actions/milestones';
import { showModal, hideModal } from '../actions/modal';
import { updateCurrentUser } from '../actions/users';
import { fetchLabels } from '../actions/labels';
import { fetchCustomFields } from 'actions/custom-fields';

import { trackEvent, startTrackSession, stopTrackSession } from '../services/events-tracker-service';
import { getUserPreference, setUserPreference } from '../services/user-preferences-service';
import { exportCSVFile } from '../utils/csv';
import { formatCurrency } from '../utils/helpers';
import { computeFieldValue } from '../utils/custom-field';
import { savePdfReport } from '../utils/pdf';
import { getMilestoneProgress, getMilestoneOpenTasksCount, getMilestoneCompletedTasksCount } from '../utils/milestone';
import { copyUrlToClipboard } from '../utils';
import { formatDate } from 'utils/date';
import { hydrate } from '../services/hydrate-service';
import { featureAvailable } from 'services/features-service';
import * as userStorageService from 'services/user-storage-service';

import { LocaleContext } from 'context/LocaleContext';
import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';

import ProjectsGantt from 'components/ProjectsGantt';
import ProjectOverview from './project-overview';
import HelpBlock from './help-block';
import CanUser from './can-user';
import ProjectHeader from 'components/ProjectHeader';
import DropdownElement from './dropdown-element';
import Tooltip from './tooltip';
import NotificationsReader from 'components/NotificationsReader';
import ViewPeriodSwitch from 'components/ViewPeriodSwitch';
import ZoomToFitButton from 'components/ZoomToFitButton';
import DraggableScrollContainer from 'components/DraggableScrollContainer';
import MilestonesActions from 'components/MilestonesActions';
import FavoriteToggle from 'components/FavoriteToggle';

import Placeholder from './placeholders/roadmap.placeholder';

import IconPlus from '../icons/icon-plus';
import IconInfo from '../icons/icon-info';
import IconShare from '../icons/icon-share';

const DEFAULT_VIEW_MODE = 'day';

class ProjectRoadmap extends Component {
    static contextType = LocaleContext;

    static propTypes = {
        projectId: PropTypes.string,
        milestones: PropTypes.array,
        loading: PropTypes.bool,
        error: PropTypes.bool,
        fetched: PropTypes.bool,
        notifications: PropTypes.array
    }

    state = {
        viewMode: getUserPreference('roadmap_view_mode', { project: this.props.projectId }) || DEFAULT_VIEW_MODE,
        detailed: (!this.props.public && getUserPreference('roadmap_detailed', { project: this.props.projectId })) || false,
        compactView: userStorageService.get('roadmap_compact_view') || false
    }

    handleViewTypeClick(type) {
        this.setState({
            detailed: type === 'swimlane'
        });

        setUserPreference('roadmap_detailed', type ==='swimlane', { project: this.props.projectId })
    }

    handleViewModeChange = viewMode => {
        trackEvent('ROADMAP.VIEW_CHANGE', {
            newView: viewMode
        });

        setUserPreference('roadmap_view_mode', viewMode, { project: this.props.projectId })

        this.setState({ viewMode });
    }

    handleShareSelect = ({ id }) => {
        switch (id) {
            case 'export':
                this.export();
                break;

            case 'savePdf':
                this.savePdf();
                break;

            case 'public':
                showModal('SHARE_LINKS', {
                    projectId: this.props.projectId,
                    type: 'milestones'
                });
                break;

            default:
                // es lint
                return null;
        }
    }

    getExportHeaders() {
        const { customFields, rollupField, t } = this.props;
        const headers = [t('milestone:Milestone')];

        if (this.tasksScreenType === 'overview') {
            headers.push(t('subteam:Portfolio'));
            headers.push(t('project:Project'));
        }

        headers.push(t('common:Progress'));
        headers.push(t('task:Open Tasks'));
        headers.push(t('task:Completed Tasks'));
        headers.push(t('common:Start Date'));
        headers.push(t('common:End Date'));
        headers.push(t('common:Story Points'));

        headers.push(...customFields.map(field => field.name));

        if (rollupField) {
            headers.push(rollupField.name);
        }

        return headers;
    }

    async getFormattedMilestones() {
        const { customFields } = this.props;

        if ((!this.props.customFieldsFetched && !this.customFieldsFetchedState) && featureAvailable('customFields')) {
            await fetchCustomFields(this.props.projectId);
            this.customFieldsFetchedState = true;

            return this.getFormattedMilestones();
        }

        const { milestones } = this.props;

        return await Promise.all(milestones.map(async milestone => {
            const formattedProps = [];

            if (milestone.task_group) {
                formattedProps.push(`hydrate(taskGroup, ${milestone.task_group}, name)`);
            } else {
                formattedProps.push(milestone.name);
            }

            if (this.tasksScreenType === 'overview') {
                formattedProps.push(`hydrate(subteam, ${this.props.projectSubteam}, name)`);
                formattedProps.push(`hydrate(project, ${milestone.project}, name)`);
            }
            formattedProps.push(`${getMilestoneProgress(milestone)}%`);
            formattedProps.push(getMilestoneOpenTasksCount(milestone));
            formattedProps.push(getMilestoneCompletedTasksCount(milestone));
            formattedProps.push(this.context.formatDate(new Date(milestone.start), this.context.dateTimeFormats.machineFriendlyFullDate));
            formattedProps.push(this.context.formatDate(new Date(milestone.end), this.context.dateTimeFormats.machineFriendlyFullDate));
            formattedProps.push(`${ milestone.statistic.completed_story_points }/${ milestone.statistic.total_story_points }`);

            formattedProps.push(...customFields.map(({ id, currency }) => {
                const value = computeFieldValue(id, {
                    resourceFields: milestone.fields,
                    allFields: customFields,
                    trackedTime: milestone.tracked_time
                });

                if (currency) {
                    return formatCurrency(value, currency);
                }

                return value;
            }));

            if (this.props.rollupField) {
                formattedProps.push(milestone.rollup.value || 0);
            }

            const result = await hydrate(formattedProps);

            return Object.keys(result).map(id => result[id] === null ? '' : result[id]);
        }));
    }

    async export() {
        const { t } = this.props;
        const headers = this.getExportHeaders();
        const formattedMilestones = await this.getFormattedMilestones();

        exportCSVFile(headers, formattedMilestones, t('common:Report_title_date', { title: this.title, date: this.context.formatDate(new Date(), this.context.dateTimeFormats.fullDate) }));
    }

    async savePdf() {
        const { t } = this.props;
        const COLUMN_WIDTHS = this.tasksScreenType === 'overview' ? [.5, .5, .5, .21, .2, .24, .2, .2] : [.5, .1, .1, .12, .2, .2];

        const formattedMilestones = await this.getFormattedMilestones();
        const headers = this.getExportHeaders();

        savePdfReport({
            headers,
            items: formattedMilestones,
            columnWidths: COLUMN_WIDTHS,
            titleText: t('common:Report_title', { title: this.title }),
            fileTitle: t('common:Report_title_date', { title: this.title, date: this.context.formatDate(new Date(), this.context.dateTimeFormats.fullDate) })
        });
    }

    handleTourStartClick = () => {
        hideModal();

        this.props.dispatch({
            type: 'TOUR_START_REQUESTED',
            payload: {
                stepId: 'milestones:milestonesHeader'
            }
        });
    }

    handleTourSkipClick = () => {
        hideModal();

        this.props.dispatch({
            type: 'TOUR_SKIP_REQUESTED',
            payload: {
                stepId: 'milestones:milestonesHeader'
            }
        });
    }

    handleStartTourClick = () => {
        this.showTourStarter();
    }

    handleHelpBlockClose = () => {
        updateCurrentUser({
            help_skipped: 'milestones'
        });
    }

    handleZoomToFitClick = () => {
        this.ganttCtrl?.zoomToFit();
    }

    showTourStarter() {
        showModal('MODAL_DEFAULT', {
            semiTransparent: true,
            type: 'tour',
            size: 'xsmall',
            title: 'Welcome to Milestones!',
            content: <p>This is where you plan out project goals.<br />Let’s begin by taking a quick tour.</p>,
            actions: <React.Fragment>
                <div className="actions-left">
                    <button className="btn btn-link btn-skip" onClick={this.handleTourSkipClick}>Skip</button>
                </div>

                <button className="btn btn-success" onClick={this.handleTourStartClick}>Start Exploring</button>
            </React.Fragment>
        });
    }

    loadMilestones() {
        fetchMilestones(this.props.projectId);
    }

    attemptFetch() {
        if (this.props.fetched || this.props.loading) {
            return;
        }

        this.loadMilestones();
    }

    get hasMilestones() {
        return this.props.milestones.length;
    }

    handleCompactChange = compactView => {
        this.setState({ compactView });
        userStorageService.set('roadmap_compact_view', compactView);
    }

    getShareOptions(canUser) {
        const { t } = this.props;

        const options = [
            {
                id: 'export',
                text: t('actions.exportAsCSV')
            },
            {
                id: 'savePdf',
                text: t('actions.exportAsPDF')
            }
        ];

        if (canUser('publicShareMilestones')) {
            options.push({
                id: 'public',
                text: t('actions.share')
            });
        }

        return options;
    }

    get title() {
        const { t, project } = this.props;
        return t('roadmapTitle', { name: project?.name || 'Nifty' });
    }

    get projects() {
        return this.props.project ? [this.props.project] : [{
            id: this.props.projectId,
            name: 'Nifty'
        }];
    }

    setGanttCtrl = ctrl => this.ganttCtrl = ctrl;

    componentDidMount() {
        this.attemptFetch();

        trackEvent('ROADMAP.VIEW');
        startTrackSession('Roadmap');

        if (this.props.public) {
            fetchLabels();
        }
    }

    componentDidUpdate(prevProps) {
        this.attemptFetch();

        if (this.props.projectId !== prevProps.projectId) {
            this.setState({
                viewMode: getUserPreference('roadmap_view_mode', { project: this.props.projectId }) || DEFAULT_VIEW_MODE
            });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (
            (this.props.projectId !== nextProps.projectId) || 
            (this.props.project !== nextProps.project) || 
            (this.props.milestones !== nextProps.milestones) || 
            (this.props.archivedMilestones !== nextProps.archivedMilestones) || 
            (this.props.loading !== nextProps.loading) || 
            (this.props.error !== nextProps.error) || 
            (this.props.fetched !== nextProps.fetched) || 
            (this.props.notifications !== nextProps.notifications) || 
            (this.props.skippedHelp !== nextProps.skippedHelp) || 
            (this.props.passedTours !== nextProps.passedTours) || 
            (this.state.viewMode !== nextState.viewMode) ||
            (this.state.detailed !== nextState.detailed) ||
            (this.state.compactView !== nextState.compactView) 
        ) return true;

        return false;
    }

    componentWillUnmount() {
        stopTrackSession('Roadmap');
    }

    render() {
        const { project, t } = this.props;

        return (
            <DocumentTitle title={`${this.title} | Nifty`}>
                <div className="project-main">
                    <NotificationsReader notifications={this.props.notifications.map(({ id }) => id)} />

                    <ProjectHeader projectId={this.props.projectId} withNav={!this.props.isPublic} withActions={!this.props.isPublic} currentNavItem="roadmap">
                        {
                            !this.props.public ? (
                                <React.Fragment>
                                    <div className="util btn-group-merged">
                                        <Tooltip tooltip={ t('common:Swimlane View') }>
                                            <button className={'btn btn-icon btn-icon-large btn-medium btn-secondary btn-border btn-square btn-colored-hover' + (this.state.detailed ? ' selected' : '')} onClick={this.handleViewTypeClick.bind(this, 'swimlane')}>
                                                <i className="material-icons">apps</i>
                                            </button>
                                        </Tooltip>

                                        <Tooltip tooltip={ t('common:Timeline View') }>
                                            <button className={'btn btn-icon btn-icon-large btn-medium btn-secondary btn-border btn-square btn-colored-hover' + (!this.state.detailed ? ' selected' : '')} onClick={this.handleViewTypeClick.bind(this, 'list')}>
                                                <i className="material-icons">format_list_bulleted</i>
                                            </button>
                                        </Tooltip>
                                    </div>
                                </React.Fragment>
                            ) : null
                        }

                        <div className="util">
                            <ViewPeriodSwitch value={this.state.viewMode} disabled={this.state.detailed} onChange={this.handleViewModeChange} />
                        </div>

                        <div className="util">
                            <ZoomToFitButton disabled={this.state.detailed} onClick={this.handleZoomToFitClick} />
                        </div>

                        <div className="util">
                            <MilestonesActions projectId={this.props.projectId} onCompactChange={this.handleCompactChange} isCompact={this.state.compactView} />
                        </div>

                        {
                            !this.props.public ? (
                                <>
                                    <div className="util">
                                        <RolesAndPermissionsContext.Consumer>
                                            {
                                                rolesAndPermission => (
                                                    <DropdownElement
                                                        items={this.getShareOptions(rolesAndPermission.canUser)}
                                                        onItemClick={this.handleShareSelect}
                                                        defaultXPosition="right"
                                                        xAlign="left"
                                                    >
                                                        <div>
                                                            <Tooltip tooltip={ t('common:importExportOptions') } withTooltipHover={false}>
                                                                <button className="btn btn-icon btn-square btn-medium btn-secondary btn-border">
                                                                    <IconShare />
                                                                </button>
                                                            </Tooltip>
                                                        </div>
                                                    </DropdownElement>
                                                )
                                            }
                                        </RolesAndPermissionsContext.Consumer>
                                    </div>

                                    <div className="util">
                                        <FavoriteToggle type="module" resourceId="milestones" projectId={this.props.projectId} />
                                    </div>

                                    {
                                        !this.props.passedTours.includes('milestones') ? (
                                            <CanUser type="canPlayTour">
                                                <div className="util">
                                                    <button className="btn btn-medium btn-text-secondary btn-icon" onClick={this.handleStartTourClick}>
                                                        <IconInfo />
                                                    </button>
                                                </div>
                                            </CanUser>
                                        ) : null
                                    }
                                </>
                            ) : null
                        }
                    </ProjectHeader>

                    <div className="project-body solid">
                        {
                            !this.props.skippedHelp.includes('milestones') ? (
                                <HelpBlock
                                    title={
                                        <React.Fragment>
                                            <CanUser type="createMilestone">
                                                <Trans i18nKey="milestone:noEntries.canCreate" />
                                            </CanUser>

                                            <CanUser type="createMilestone" reverse={true}>
                                                { t('noEntries.cantCreate') }
                                            </CanUser>
                                        </React.Fragment>
                                    }
                                    readUrl="https://help.niftypm.com/en/articles/1382624-how-to-use-project-milestones-in-nifty"
                                    watchUrl="https://youtu.be/gV7lIfJGedQ"
                                    onClose={this.handleHelpBlockClose}
                                />
                            ) : null
                        }

                        {
                            (this.props.loading && !this.props.shouldRefetch) ? (
                                <div className="project-roadmap-placeholder-wrap">
                                    {
                                        !this.props.loading ? (
                                            <div className="milestone-bar-holder">
                                                <div className="milestone-bar" onClick={this.handleAddMilestoneClick}>
                                                    <IconPlus /> { t('Create a milestone') }
                                                </div>
                                            </div>
                                        ) : null
                                    }
                                    <Placeholder static={!this.props.loading} />
                                </div>
                            ) : null
                        }

                        {
                            this.state.detailed ? (
                                <DraggableScrollContainer className="project-roadmap nice-scroll">
                                    <ProjectOverview id={this.props.projectId} />
                                </DraggableScrollContainer>
                            ) : (
                                <div className="project-roadmap no-scroll">
                                    <ProjectsGantt
                                        id={this.props.projectId}
                                        dontFetch={true}
                                        compactView={this.state.compactView}
                                        single={true}
                                        isPublic={this.props.public}
                                        loading={this.props.loading}
                                        viewMode={this.state.viewMode}
                                        projects={this.projects}
                                        createMilestoneProjectId={this.props.projectId}
                                        milestones={this.props.milestones}
                                        archivedMilestones={this.props.archivedMilestones}
                                        onViewModeChange={this.handleViewModeChange}
                                        provideController={this.setGanttCtrl}
                                    />
                                </div>
                            )
                        }
                    </div>
                </div>
            </DocumentTitle>
        );
    }
}

const makeMapStateToProps = () => {
    const getProject = makeGetProject();
    const getProjectNotificationsByType = makeGetProjectNotificationsByType();
    const getSortedActiveProjectMilestones = makeGetSortedActiveProjectMilestones();
    const getSortedArchivedProjectMilestones = makeGetSortedArchivedProjectMilestones();
    const getSkippedHelp = makeGetSkippedHelp();
    const getMilestonesFetched = makeGetFetchedState();
    const getMilestonesLoading = makeGetLoadingState();
    const getCurrentUser = makeGetCurrentUser();
    const getCustomFields = makeGetCustomFields();
    const getCustomFieldsFetched = makeGetFetchedState();
    const getCustomField = makeGetCustomField();

    return ({ projects, milestones, notifications, appProps, users, customFields }, ownProps) => {
        const projectId = ownProps.projectId || ownProps.match?.params.projectId;
        const project = getProject(projects.items, projectId);

        if (ownProps.isPublic) {
            return {
                public: true,
                projectId,
                project,
                milestones: getSortedActiveProjectMilestones(milestones.items, projectId),
                archivedMilestones: getSortedArchivedProjectMilestones(milestones.items, projectId),
                passedTours: ['milestones'],
                skippedHelp: ['milestones'],
                customFields: [],
                loading: getMilestonesLoading(milestones.fetchInfo, 'project', projectId),
                fetched: getMilestonesFetched(milestones.fetchInfo, 'project', projectId),
                notifications: []
            };
        }

        const currentUser = getCurrentUser(users);

        return {
            projectId,
            project,
            milestones: getSortedActiveProjectMilestones(milestones.items, projectId),
            archivedMilestones: getSortedArchivedProjectMilestones(milestones.items, projectId),
            passedTours: ['milestones'],
            customFields: getCustomFields(customFields.items, currentUser.role, 'milestone', projectId),
            rollupField: project?.milestones_rollup?.fieldId && getCustomField(customFields.items, project.milestones_rollup.fieldId),
            skippedHelp: getSkippedHelp(appProps),
            loading: getMilestonesLoading(milestones.fetchInfo, 'project', projectId),
            fetched: getMilestonesFetched(milestones.fetchInfo, 'project', projectId),
            notifications: getProjectNotificationsByType(notifications.items, projectId, 'milestone'),
            customFieldsFetched: getCustomFieldsFetched(customFields.fetchInfo, 'project', projectId)
        }
    }
}

export default withRouter(connect(makeMapStateToProps)(withTranslation(['milestone', 'common', 'date', 'task', 'subteam', 'project'])(ProjectRoadmap)));
